import { FC, useState } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { StayDataType } from "data/types";
import { Link } from "react-router-dom";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import moment from "moment";
import { currencySymbol } from "shared/CurrencySymbol";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import successHandler from "utils/helpers/SuccessHandler";
import { useDispatch } from "react-redux";
import Invoice from "../../../src/containers/Booking/Invoice";
import InvoiceUser from "../../../src/containers/Booking/InvoiceUser";
import {
  setReserverBooking,
  updateBookingsDetails,
} from "../../redux/reducers/Payments/PaymentReducer";
import { deleteInvoice } from "../../redux/reducers/Properties/PropertiesSlice";
import Badge from "shared/Badge/Badge";

export interface StayCardProps {
  className?: string;
  data?: StayDataType;
  size?: "default" | "small";
  booking?: any;
  fetchBookings?: any;
}

const StayCardV3: FC<StayCardProps> = ({
  size = "default",
  className = "",
  booking,
  fetchBookings,
}) => {
  const { property } = booking;
  const { currentUser } = useSelector((state: RootState) => state.user);
  const {
    place_images: placeImagesTemp = property?.place_images,
    title = property?.title,
    href = `/booking_request?id=${booking?.id}`,
    price = booking?.total_amount,
    id = property?.id,
    check_in_date = booking?.check_in_date,
    check_out_date = booking?.check_out_date,
    status = booking?.status,
    guest = booking?.number_of_guests,
    request_by = booking?.request_by,
    invoice = booking?.invoice_no,
    PropertyStatus = booking?.is_shared_property,
    Room = booking?.booked_room,
    roomCount = booking?.property.rooms_count
  } = property;

  const place_images = placeImagesTemp;
  const sortAddress = `${property?.city}, ${property?.state}`;
  const dispatch = useDispatch<AppDispatch>();
  const [showInvoice, setShowInvoice] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const openInvoice = () => setShowInvoice(true);
  const closeInvoice = () => setShowInvoice(false);
  const [showUserInvoice, setShowUserInvoice] = useState(false);
  const user = currentUser?.is_student

  const modifiedPropertyStatus = (
    PropertyStatus: string | undefined | boolean
  ) => {
    const status =
      typeof PropertyStatus === "boolean"
        ? PropertyStatus.toString()
        : PropertyStatus;
    switch (status) {
      case "true":
        return "Shared Property";
      case "false":
        return "Entire Property";
      default:
        return "";
    }
  };

  const getStatusBadge = (
    state: string | undefined,
    is_student: boolean = false
  ) => {
    switch (state) {
      case "invoice_sent":
        return {
          label: is_student ? "Invoice received" : "Invoice sent",
          className: "bg-yellow-100 text-yellow-800",
        };
      case "booking_request":
        return {
          label: "Booking request",
          className: "bg-blue-100 text-blue-800",
        };
      case "canceled":
        return {
          label: "Canceled",
          className: "bg-red-100 text-red-800",
        };
      default:
        return {
          label: "",
          className: "",
        };
    }
  };

  const issueInvoiceHandler = () => {
    dispatch(setReserverBooking(booking));
    setShowUserInvoice((prev) => !prev);
  };

  const handleDeleteInvoice = async () => {
    if (id == null) {
      return;
    }
    setIsDeleting(true);
    const postData = {
      bookingId: booking?.id,
      id: booking?.invoice_no,
      _destroy: true,
      status: "canceled",
    };

    try {
      const action = await dispatch(deleteInvoice(postData));
      if (deleteInvoice.fulfilled.match(action)) {
        successHandler("Invoice deleted successfully:", action.payload);
        await fetchBookings();
      } else {
        console.error("Failed to delete invoice:", action.payload);
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelBooking = async () => {
    if (id == null) {
      return;
    }

    const actionData = {
      id: booking?.id,
      status: "canceled",
    };
    try {
      const action = await dispatch(updateBookingsDetails(actionData));
      if (updateBookingsDetails.fulfilled.match(action)) {
        successHandler("Booking canceled successfully:", action.payload);
        await fetchBookings();
      } else {
        console.error("Failed to cancel booking:", action.payload);
      }
    } catch (error) {
      console.error("Error canceling booking:", error);
    }
  };

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full">
        <GallerySlider
          uniqueID={`StayCard_${id}`}
          ratioClass="aspect-w-4 aspect-h-3"
          galleryImgs={place_images || []}
          href={href}
        />
        <div
          className={`absolute left-3 top-3 z-[1] px-5 rounded-md ${getStatusBadge(status, user).className
            }`}
        >
          {getStatusBadge(status, user).label}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={size === "default" ? "p-4 space-y-4" : "p-3 space-y-2"}>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <h1 className={`font-medium capitalize text-lg`}>
              <span className="line-clamp-1">{title}</span>
            </h1>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between my-4">
              <Badge
                name={modifiedPropertyStatus(PropertyStatus) || "Property"}
                className="text-xs"
              />
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <span className="mr-2">INVOICE:</span>{" "}
                {size === "default" && (
                  <span className="text-sm ">{booking?.invoice_no || "-"}</span>
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2">
            {size === "default" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            )}
            <span className="">{sortAddress || "No address found"}</span>
          </div>
        </div>
        <hr />
        <div style={{ height: "182px" }}>
          {(PropertyStatus === true || PropertyStatus === "true") && (
            <div className="flex items-center mb-5">
              <>
                <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                  Room Name :
                </span>
                <div className="text-sm font-semibold line-clamp-1">
                  {Room || "any"}
                </div>
              </>
            </div>
          )}
          {(PropertyStatus !== true && PropertyStatus !== "true") && (
            <div className="flex items-center mb-5">
              <>
                <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                  Total Rooms :
                </span>
                <div className="text-sm font-semibold line-clamp-1">
                  {roomCount || "any"}
                </div>
              </>
            </div>
          )}
          {currentUser?.is_host && (
            <div className="flex items-center mb-5">
              <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                Request by :
              </span>
              {size === "default" && (
                <span className="text-sm font-semibold">{request_by}</span>
              )}
            </div>
          )}
          <div className="flex items-center mb-5">
            <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
              Period :
            </span>
            {size === "default" && (
              <span className="text-sm font-semibold">
              
{moment.utc(check_in_date).format("MMM DD, YYYY")} to{" "}
{moment.utc(check_out_date).format("MMM DD, YYYY")}
              </span>
            )}
          </div>
          <div className="flex items-center mb-5">
            <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
              Guest :
            </span>
            {size === "default" && (
              <span className="text-sm font-semibold">{guest}</span>
            )}
          </div>
          <div className="flex items-center mb-5">
            <span className="min-w-[110px] text-sm text-neutral-500 dark:text-neutral-400 font-normal">
              Amount :
            </span>
            <span className="text-sm font-semibold">
              {`${currencySymbol()} ${Number(price)?.toFixed(2) || "N/A"}`}
            </span>
            {/* {!!reviewStart && <StartRating reviewCount={reviewCount} point={reviewStart} />} */}
          </div>
        </div>
        <hr />
      </div>
    );
  };

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-xl transition-shadow ${className}`}
      data-nc-id="StayCard"
    >
      {renderSliderGallery()}
      <Link to={href}> {renderContent()}</Link>
      <div className="pl-3 pb-3 pr-3 rounded-lg">
        <div className="flex gap-4 buttonsStyle">
          {booking?.status === "canceled" ? (
            <div className="w-full text-center text-red-500 font-semibold text-lg">
              Request Declined
            </div>
          ) : booking?.status === "confirmed" ? (
            <>
              <ButtonPrimary
                className="bg-green-500 text-white px-6 py-3 rounded-lg cursor-not-allowed"
                disabled
              >
                Confirmed
              </ButtonPrimary>

              {currentUser?.is_student && booking?.property?.slug && (
                <ButtonPrimary
                  href={`/listing-stay-detail/${booking.property.slug}`}
                  className="bg-orange-500 text-white px-6 py-3 rounded-lg text-xs"
                >
                  Visit
                </ButtonPrimary>
              )}
            </>
          ) : currentUser?.is_host ? (
            <>
              {booking?.status !== "canceled" &&
                (booking?.status === "invoice_sent" ? (
                  <>
                    <ButtonPrimary
                      className="bg-gray-500 text-white px-6 py-3 rounded-lg cursor-not-allowed"
                      disabled
                    >
                      Invoice
                    </ButtonPrimary>

                    {booking?.invoice_no != null && (
                      <ButtonPrimary
                        onClick={handleDeleteInvoice}
                        disabled={isDeleting}
                        className={`bg-red-500 text-white px-6 py-3 rounded-lg text-xs ${isDeleting
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-red-600"
                          }`}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </ButtonPrimary>
                    )}
                  </>
                ) : (
                  <ButtonPrimary
                    className="bg-orange-500 text-white px-6 py-3 rounded-lg text-xs w-full"
                    onClick={() => setShowInvoice((prev) => !prev)}
                  >
                    {showInvoice ? "Close Invoice" : "Issue Invoice"}
                  </ButtonPrimary>
                ))}

              <ButtonPrimary
                className="bg-orange-500 text-white px-6 py-3 rounded-lg text-xs w-full"
                onClick={handleCancelBooking}
                disabled={booking?.status === "canceled"}
              >
                Reject
              </ButtonPrimary>
            </>
          ) : (
            <>
              {booking?.status === "canceled" ? (
                <button
                  className="bg-gray-300 dark:bg-gray-600 px-6 py-3 rounded-lg text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  disabled
                >
                  Pending
                </button>
              ) : booking?.status === "booking_request" ? (
                <button
                  className="bg-gray-300 dark:bg-gray-600 px-6 py-3 rounded-lg text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  disabled
                >
                  Pending
                </button>
              ) : booking?.status === "invoice_sent" ? (
                <button
                  className="bg-green-500 text-white px-6 py-3 rounded-lg w-full"
                  onClick={issueInvoiceHandler}
                >
                  {showUserInvoice ? "Hide" : "Pay"}
                </button>
              ) : null}

              <ButtonPrimary
                className=" rounded-lg font-light w-full"
                onClick={handleCancelBooking}
                disabled={booking?.status === "canceled"}
              >
                Cancel
              </ButtonPrimary>

              {booking?.status !== "canceled" && (
                <ButtonPrimary
                  href={`/listing-stay-detail/${booking?.property?.slug}`}
                  className=" rounded-lg font-light w-full"
                >
                  Visit
                </ButtonPrimary>
              )}
            </>
          )}
        </div>

        {showUserInvoice && (
          <InvoiceUser
            bookingRequestDetails={booking}
            setShowUserInvoice={setShowUserInvoice}
          />
        )}
        {showInvoice && (
          <Invoice
            bookingRequestDetails={booking}
            closeInvoice={closeInvoice}
            callback={fetchBookings}
          />
        )}
      </div>
    </div>
  );
};

export default StayCardV3;
